import { Form, Button, Row, Col } from 'react-bootstrap';
import { Search, X } from 'lucide-react';
import type { AlertFilter, CandidateFilter } from '../models/types';

export type MatchTypeFilter = 'matched' | 'exclusive' | 'machineUnmatched' | 'humanUnmatched';

const SKU_LAST_DIGITS = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9'];

interface CompetitorProductFiltersProps {
  readonly searchQuery: string;
  readonly onSearchQueryChange: (query: string) => void;
  readonly matchType: MatchTypeFilter;
  readonly onMatchTypeChange: (matchType: MatchTypeFilter) => void;
  readonly alertFilter: AlertFilter;
  readonly onAlertFilterChange: (alertFilter: AlertFilter) => void;
  readonly candidateFilter: CandidateFilter;
  readonly onCandidateFilterChange: (candidateFilter: CandidateFilter) => void;
  readonly skuLastDigit: string;
  readonly onSkuLastDigitChange: (skuLastDigit: string) => void;
  readonly actionedBy: string;
  readonly onActionedByChange: (actionedBy: string) => void;
  readonly reviewers: readonly string[];
  readonly onSearch: () => void;
  readonly onClear: () => void;
  readonly isLoading: boolean;
}

export function CompetitorProductFilters({
  searchQuery,
  onSearchQueryChange,
  matchType,
  onMatchTypeChange,
  alertFilter,
  onAlertFilterChange,
  candidateFilter,
  onCandidateFilterChange,
  skuLastDigit,
  onSkuLastDigitChange,
  actionedBy,
  onActionedByChange,
  reviewers,
  onSearch,
  onClear,
  isLoading,
}: CompetitorProductFiltersProps) {
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      onSearch();
    }
  };

  return (
    <div className="py-2" style={{ borderTop: '1px solid #444' }}>
      <Row className="g-2 align-items-end">
        <Col xs={6} md={3}>
          <Form.Label style={{ fontSize: '0.75rem', marginBottom: '0.25rem', color: '#aaa' }}>Match Status</Form.Label>
          <Form.Select
            size="sm"
            value={matchType}
            onChange={(e) => onMatchTypeChange(e.target.value as MatchTypeFilter)}
            style={{ backgroundColor: '#333', color: '#fff', border: '1px solid #555' }}
          >
            <option value="matched">Matched</option>
            <option value="exclusive">Exclusive</option>
            <option value="machineUnmatched">Machine Unmatched</option>
            <option value="humanUnmatched">Human Unmatched</option>
          </Form.Select>
        </Col>
        <Col xs={6} md={3}>
          <Form.Label style={{ fontSize: '0.75rem', marginBottom: '0.25rem', color: '#aaa' }}>Alert Filter</Form.Label>
          <Form.Select
            size="sm"
            value={alertFilter}
            onChange={(e) => onAlertFilterChange(e.target.value as AlertFilter)}
            style={{ backgroundColor: '#333', color: '#fff', border: '1px solid #555' }}
          >
            <option value="all">All</option>
            <option value="price_anomaly">Price Anomaly</option>
            <option value="size_not_comparable">Size Not Comparable</option>
            <option value="no_alerts">No Alerts</option>
          </Form.Select>
        </Col>
        {matchType === 'humanUnmatched' && (
          <Col xs={6} md={3}>
            <Form.Label style={{ fontSize: '0.75rem', marginBottom: '0.25rem', color: '#aaa' }}>Candidates</Form.Label>
            <Form.Select
              size="sm"
              value={candidateFilter}
              onChange={(e) => onCandidateFilterChange(e.target.value as CandidateFilter)}
              style={{ backgroundColor: '#333', color: '#fff', border: '1px solid #555' }}
              title="Work the products that have competitor candidates first"
            >
              <option value="all">All</option>
              <option value="with_candidates">Has candidates</option>
              <option value="zero_candidates">No candidates</option>
            </Form.Select>
          </Col>
        )}
        {matchType === 'matched' && (
          <Col xs={6} md={3}>
            <Form.Label style={{ fontSize: '0.75rem', marginBottom: '0.25rem', color: '#aaa' }}>Actioned By</Form.Label>
            <Form.Select
              size="sm"
              value={actionedBy}
              onChange={(e) => onActionedByChange(e.target.value)}
              style={{ backgroundColor: '#333', color: '#fff', border: '1px solid #555' }}
              title="Show only the SKUs a given reviewer actioned"
            >
              <option value="">Anyone</option>
              {reviewers.map((reviewer) => (
                <option key={reviewer} value={reviewer}>
                  {reviewer}
                </option>
              ))}
            </Form.Select>
          </Col>
        )}
        {matchType !== 'matched' && (
          <Col xs={6} md={3}>
            <Form.Label style={{ fontSize: '0.75rem', marginBottom: '0.25rem', color: '#aaa' }}>SKU Ends With</Form.Label>
            <Form.Select
              size="sm"
              value={skuLastDigit}
              onChange={(e) => onSkuLastDigitChange(e.target.value)}
              style={{ backgroundColor: '#333', color: '#fff', border: '1px solid #555' }}
              title="Split the queue between reviewers by the last digit of the SKU"
            >
              <option value="">Any</option>
              {SKU_LAST_DIGITS.map((digit) => (
                <option key={digit} value={digit}>
                  {digit}
                </option>
              ))}
            </Form.Select>
          </Col>
        )}
      </Row>
      <Row className="g-2 align-items-end mt-0">
        <Col xs={12} md={9}>
          <Form.Label style={{ fontSize: '0.75rem', marginBottom: '0.25rem', color: '#aaa' }}>Search (SKU, Brand, Name)</Form.Label>
          <Form.Control
            size="sm"
            type="text"
            placeholder="Search by SKU, brand, or name..."
            value={searchQuery}
            onChange={(e) => onSearchQueryChange(e.target.value)}
            onKeyDown={handleKeyDown}
            style={{ backgroundColor: '#333', color: '#fff', border: '1px solid #555' }}
          />
        </Col>
        <Col xs={12} md={3} className="d-flex gap-2 justify-content-end">
          {searchQuery && (
            <Button variant="outline-secondary" size="sm" onClick={onClear} disabled={isLoading}>
              <X size={14} />
            </Button>
          )}
          <Button variant="primary" size="sm" onClick={onSearch} disabled={isLoading}>
            <Search size={14} className="me-1" />
            {isLoading ? '...' : searchQuery ? 'Search' : 'List All'}
          </Button>
        </Col>
      </Row>
    </div>
  );
}
